import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, MenuIcon, HeartIcon, GlobeIcon, MessageCircleIcon, PhoneIcon, DollarSignIcon, XIcon, TrendingUpIcon, PlusIcon, LogOutIcon, PackageIcon, ClipboardListIcon, HomeIcon, MapPinIcon, UserPlusIcon, ChevronRightIcon, TrashIcon, ChevronDownIcon, TruckIcon, ShieldCheckIcon, ClockIcon, StethoscopeIcon } from 'lucide-react';
import { MobileMenu } from '../MobileMenu';
import { AuthModal } from '../modals/AuthModal';
import { LocationModal } from '../modals/LocationModal';
import { AnnouncementBar } from './AnnouncementBar';
import { MobileDeliverToPill } from './MobileDeliverToPill';
import { OrderTrackingModal } from '../modals/OrderTrackingModal';
import { trackViewCart } from '../../lib/analytics/events';
import { useStore } from '../../lib/store';
import { motion, AnimatePresence } from 'framer-motion';
import { DesktopHeader } from './DesktopHeader';
import { POPULAR_SEARCHES, TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../../data/searchData';
interface HeaderProps {
  locationModalOpen: boolean;
  setLocationModalOpen: (open: boolean) => void;
  selectedLocation: {
    name: string;
  } | null;
  setSelectedLocation: (location: {
    name: string;
  } | null) => void;
  disableShrink?: boolean;
  showAnnouncement?: boolean;
}
export function Header({
  locationModalOpen,
  setLocationModalOpen,
  selectedLocation,
  setSelectedLocation,
  disableShrink = false,
  showAnnouncement = false
}: HeaderProps) {
  const navigate = useNavigate();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    language,
    setLanguage,
    cartCount,
    cartTotal,
    removeFromCart,
    updateQuantity,
    searchQuery,
    setSearchQuery,
    addToCart,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const wishlistCount = wishlistItems.length;
  const [scrolled, setScrolled] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [mobileSearchExpanded, setMobileSearchExpanded] = useState(false);
  const searchInputRef = useRef(null);
  const searchDropdownRef = useRef(null);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWishlistMenu, setShowWishlistMenu] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);
  const isMobile = windowWidth < 768;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [orderTrackingModalOpen, setOrderTrackingModalOpen] = useState(false);
  // Track window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);
  // Add click outside listener to close search dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target) && searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen || mobileSearchExpanded) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen, mobileSearchExpanded]);
  const handleSearchInputFocus = () => {
    setShowSearchDropdown(true);
  };
  const handleSearchInputChange = e => {
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };
  const handleRemoveRecentSearch = search => {
    // Remove by clearing all and re-adding the rest
    const filtered = recentSearches.filter(item => item !== search);
    clearRecentSearches();
    filtered.forEach(s => addRecentSearch(s));
  };
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  // Calculate cart total
  const cartTotalValue = cartTotal();
  const getSearchContent = () => {
    const isSearching = searchQuery.length > 0;
    return <div className="flex flex-col md:flex-row gap-6" data-id="element-2976" data-mp="2lk3m2op33sp5">
        {/* Left Column: Suggestions & Categories */}
        <div className="flex-1 space-y-6" data-id="element-2977" data-mp="2qioql0bdcyje">
          {!isSearching ? <>
              {recentSearches.length > 0 && <div data-id="element-2978" data-mp="2vcj75uvogxe9">
                  <div className="flex items-center justify-between mb-3" data-id="element-2979" data-mp="2zc1q4l3xuyeg">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-2980" data-mp="2ze4fr416ipin">
                      Recent Searches
                    </h3>
                    <button onClick={() => clearRecentSearches()} className="text-xs text-[#1E2B55] hover:underline font-medium" data-id="element-2981" data-mp="399xxd1tr997a">
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2" data-id="element-2982" data-mp="1jv00ttmswebh">
                    {recentSearches.map((search, index) => <motion.div key={index} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex items-center px-3 py-1.5 rounded-lg bg-gray-50 text-sm hover:bg-gray-100 transition-colors cursor-pointer border border-gray-100 group" onClick={() => setSearchQuery(search)} data-id="element-2983" data-mp="1bvmvla1ahlyk" data-mpi={index}>
                        <ClockIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-2984" data-mp="24hxv0hd5tgts" />
                        <span className="text-gray-700" data-id="element-2985" data-mp="1zjwcbxjnbjiu">{search}</span>
                        <button onClick={e => {
                  e.stopPropagation();
                  handleRemoveRecentSearch(search);
                }} className="ml-2 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2986" data-mp="2djxdle3tosyu">
                          <XIcon className="w-3 h-3" data-id="element-2987" data-mp="2x4xvif2fqoz2" />
                        </button>
                      </motion.div>)}
                  </div>
                </div>}

              <div data-id="element-2988" data-mp="2o37x2pa4yprs">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2989" data-mp="29g4x8bycc51a">
                  Trending Searches
                </h3>
                <div className="grid grid-cols-2 gap-2" data-id="element-2990" data-mp="2e7mzr4g0idod">
                  {TRENDING_SEARCHES.map((search, index) => <button key={index} onClick={() => setSearchQuery(search)} className="flex items-center p-2 rounded-lg hover:bg-blue-50 transition-colors text-sm text-gray-700 group" data-id="element-2991" data-mp="20hb44vk7xwsh" data-mpi={index}>
                      <TrendingUpIcon className="w-4 h-4 mr-3 text-blue-500 group-hover:scale-110 transition-transform" data-id="element-2992" data-mp="1vf29lurp6mo9" />
                      {search}
                    </button>)}
                </div>
              </div>

              <div data-id="element-2993" data-mp="25xh9pn5epgf1">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-2994" data-mp="21xgkr8kolmtq">
                  Top Categories
                </h3>
                <div className="grid grid-cols-2 gap-3" data-id="element-2995" data-mp="1wy3z2r5jqlsq">
                  {trendingCategories.slice(0, 4).map((cat, index) => <button key={index} onClick={() => navigate(`/category/${cat.name.toLowerCase().replace(/ /g, '-')}`)} className="flex items-center gap-3 p-2 rounded-xl border border-gray-100 hover:border-[#1E2B55] hover:bg-blue-50 transition-all group" data-id="element-2996" data-mp="1n8sqd5mknj5d" data-mpi={index}>
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100" data-id="element-2997" data-mp="14azps9zf7lb5">
                        <img src={cat.icon} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform" data-id="element-2998" data-mp="3fw8ift1r7xs0" />
                      </div>
                      <span className="text-sm font-medium text-gray-700" data-id="element-2999" data-mp="3twt36ur8c3o5">
                        {cat.name}
                      </span>
                    </button>)}
                </div>
              </div>
            </> : <div className="space-y-4" data-id="element-3000" data-mp="32652tkxdr2bj">
              <div data-id="element-3001" data-mp="367qnhsg5okwa">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3002" data-mp="3678fxzo1mk92">
                  Suggestions
                </h3>
                <div className="space-y-1" data-id="element-3003" data-mp="3b0bdeivxyduj">
                  {wheyRelatedSearches.filter(s => s.includes(searchQuery.toLowerCase())).slice(0, 5).map((search, index) => <button key={index} onClick={() => setSearchQuery(search)} className="flex items-center w-full p-2 rounded-lg hover:bg-gray-50 text-sm text-gray-700" data-id="element-3004" data-mp="20x9ed1tftj9u" data-mpi={index}>
                        <SearchIcon className="w-4 h-4 mr-3 text-gray-400" data-id="element-3005" data-mp="29vldxtca7ref" />
                        <span className="flex-1 text-left" data-id="element-3006" data-mp="2evpu1i9a2k44">
                          {search.split(new RegExp(`(${searchQuery})`, 'gi')).map((part, i) => part.toLowerCase() === searchQuery.toLowerCase() ? <span key={i} className="font-bold text-[#1E2B55]" data-id="element-3007" data-mp="1vxn0cq5sbyzz">
                                  {part}
                                </span> : part)}
                        </span>
                      </button>)}
                </div>
              </div>

              <div data-id="element-3008" data-mp="lz40m12fw2sx">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3009" data-mp="lylzaq0x6ye5">
                  Related Categories
                </h3>
                <div className="grid grid-cols-1 gap-2" data-id="element-3010" data-mp="cyytzt52l8ft">
                  {wheyRelatedCategories.slice(0, 3).map((cat, index) => <button key={index} onClick={() => navigate(`/category/${cat.name.toLowerCase().replace(/ /g, '-')}`)} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group" data-id="element-3011" data-mp="87yvijgayib7" data-mpi={index}>
                      <span className="text-sm text-gray-700" data-id="element-3012" data-mp="3leii6dqvw0o0">{cat.name}</span>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400 group-hover:translate-x-1 transition-transform" data-id="element-3013" data-mp="3brelixexafzm" />
                    </button>)}
                </div>
              </div>
            </div>}
        </div>

        {/* Right Column: Featured Products */}
        <div className="w-full md:w-[320px] border-t md:border-t-0 md:border-l border-gray-100 pt-6 md:pt-0 md:pl-6" data-id="element-3014" data-mp="36rtkmhg6zxk9">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4" data-id="element-3015" data-mp="3uhe73abk1cya">
            {isSearching ? 'Matching Products' : 'Trending Products'}
          </h3>
          <div className="space-y-4" data-id="element-3016" data-mp="3ks4dtr4z5o8l">
            {TRENDING_PRODUCTS.map(product => <motion.div key={product.id} initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-xl transition-colors group cursor-pointer" onClick={() => navigate(`/product/${product.id}`)} data-id="element-3017" data-mp="3pqxtnq7qhrbx">
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 border border-gray-100" data-id="element-3018" data-mp="21swp5cap679v">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-3019" data-mp="2fme9gzb7jo0y" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-3020" data-mp="1ivm2qsa5z8bu">
                  <h4 className="text-sm font-bold text-gray-900 line-clamp-2 leading-tight mb-1 group-hover:text-[#1E2B55] transition-colors" data-id="element-3021" data-mp="1wu27wesdoqrv">
                    {product.name}
                  </h4>
                  <div className="flex items-center gap-2" data-id="element-3022" data-mp="3898ovht63x7k">
                    <span className="text-sm font-black text-[#1E2B55]" data-id="element-3023" data-mp="387nw9xqeyocc">
                      {product.price.toFixed(2)} SAR
                    </span>
                    {product.discount > 0 && <span className="text-[10px] text-gray-400 line-through" data-id="element-3024" data-mp="2grcmty8ugof">
                        {product.originalPrice.toFixed(2)}
                      </span>}
                  </div>
                </div>
                <button onClick={e => {
              e.stopPropagation();
              haptics.success();
              addToCart({
                id: product.id,
                name: product.name,
                price: product.price,
                image: product.image
              });
              toast.success(`${product.name} added to cart!`);
            }} className="w-8 h-8 flex items-center justify-center bg-blue-50 text-[#1E2B55] rounded-full hover:bg-[#1E2B55] hover:text-white transition-all shadow-sm" data-id="element-3025" data-mp="hc7cxgpf38t9">
                  <PlusIcon className="w-4 h-4" data-id="element-3026" data-mp="194eq275ps32w" />
                </button>
              </motion.div>)}
          </div>

          {isSearching && <button className="w-full mt-6 py-3 text-sm font-bold text-white bg-[#1E2B55] hover:bg-[#162041] rounded-xl transition-all shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2 group" onClick={() => {
          if (searchQuery.trim()) {
            addRecentSearch(searchQuery.trim());
          }
          navigate(`/search?q=${searchQuery}`);
        }} data-id="element-3027" data-mp="2pju0zwz7ikrg">
              <span data-id="element-3028" data-mp="38atvtetduosn">View All Results</span>
              <ChevronRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-3029" data-mp="33jljob0o4pbs" />
            </button>}
        </div>
      </div>;
  };
  // Handle opening the auth modal
  const openAuthModal = () => {
    setAuthModalOpen(true);
    // Close any other open menus
    setShowAccountMenu(false);
  };
  return <>
      {/* Desktop Header */}
      <DesktopHeader locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} disableShrink={disableShrink} showAnnouncement={showAnnouncement} data-id="element-3030" data-mp="3r735zr15yv7d" />

      {/* Mobile Header */}
      <header className="fixed top-0 left-0 right-0 z-[100] m-0 p-0 md:hidden" data-id="element-3031" data-mp="1kknq1y6am5pm">
        {/* Mobile: Deliver To Pill */}
        <MobileDeliverToPill selectedLocation={selectedLocation} onOpenLocationModal={() => setLocationModalOpen(true)} data-id="element-3032" data-mp="15nddelhwytsb" />

        {/* Main Header - Mobile */}
        <div className="bg-white shadow-md transition-all duration-300" data-id="element-3033" data-mp="wnhxy4hbscmh">
          <div className="container mx-auto px-4" data-id="element-3034" data-mp="11m1mmcnb02ij">
            <div className="flex items-center justify-between h-14" data-id="element-3035" data-mp="11mukeo4abhoj">
              {/* Logo and Menu Button */}
              <div className="flex items-center" data-id="element-3036" data-mp="2r1iweo8yqxm1">
                <button className="mr-3" onClick={toggleMobileMenu} data-id="element-3037" data-mp="2vzsw9c5mumdz">
                  <MenuIcon className="w-6 h-6 text-gray-700" data-id="element-3038" data-mp="2vxq52osqun7o" />
                </button>
                <button onClick={() => navigate('/')} className="mr-4" data-id="element-3039" data-mp="3649ay27dlxg7">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="Innova Pharmacy" className="h-9" data-id="element-3040" data-mp="3167nz66txdzg" />
                </button>
              </div>

              {/* User Actions - Mobile */}
              <div className="flex items-center space-x-3" data-id="element-3041" data-mp="3osny2eekorbq">
                <button onClick={() => setMobileSearchExpanded(!mobileSearchExpanded)} data-id="element-3042" data-mp="3jw78dfrq64wj">
                  <SearchIcon className="w-6 h-6 text-gray-700" data-id="element-3043" data-mp="1ohrfj7u4r83" />
                </button>

                <div className="relative" onClick={() => navigate('/cart')} data-id="element-3044" data-mp="3tuwtdhex45h0">
                  <ShoppingCartIcon className="w-6 h-6 text-gray-700" data-id="element-3045" data-mp="23ge44w5qo74g" />
                  <span className="absolute -top-1 -right-1 bg-[#1E2B55] text-white text-[10px] font-bold rounded-full flex items-center justify-center w-5 h-5" data-id="element-3046" data-mp="1zfkhe6418yd5">
                    {cartCount()}
                  </span>
                </div>

                <div className="relative" onClick={() => navigate('/account/wishlist')} data-id="element-3047" data-mp="2syk87008hfr6">
                  <HeartIcon className="w-6 h-6 text-gray-700" data-id="element-3048" data-mp="2xsodzfxrjhqo" />
                  <span className="absolute -top-1 -right-1 bg-[#1E2B55] text-white text-[10px] font-bold rounded-full flex items-center justify-center w-5 h-5" data-id="element-3049" data-mp="31qm6osqqslyt">
                    {wishlistCount}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Expandable Search Bar */}
        <div className={`fixed left-0 right-0 bg-white border-b border-gray-200 shadow-md overflow-hidden transition-all duration-300 ease-in-out z-[90] ${mobileSearchExpanded ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`} style={{
        top: '112px'
      }} data-id="element-3050" data-mp="2j1631wjrgde6">
          <div className="px-4 py-2" data-id="element-3051" data-mp="8euafx7txs5o">
            <div className="relative" data-id="element-3052" data-mp="8dssd8sec5eg">
              <input type="text" placeholder="Search for medicines..." className="w-full border border-gray-300 rounded-lg py-2.5 pl-4 pr-20 focus:outline-none focus:ring-2 focus:ring-[#1E2B55]" value={searchQuery} onChange={handleSearchInputChange} onFocus={handleSearchInputFocus} data-id="element-3053" data-mp="8gyimrcrhdbr" />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2" data-id="element-3054" data-mp="3lrnqe44i984v">
                {searchQuery && <button onClick={handleClearSearch} className="text-gray-400" data-id="element-3055" data-mp="3bwvquurd4b7g">
                    <XIcon className="w-4 h-4" data-id="element-3056" data-mp="37uqydzcnhkme" />
                  </button>}
                <button className="text-gray-500" data-id="element-3057" data-mp="1a47k4bfif5mx">
                  <SearchIcon className="w-5 h-5" data-id="element-3058" data-mp="1a3odvrz825oq" />
                </button>
                <button onClick={() => setMobileSearchExpanded(false)} className="text-gray-500 p-1" data-id="element-3059" data-mp="163xgv6j3qtbr">
                  <XIcon className="w-5 h-5" data-id="element-3060" data-mp="34sg9hbfdfm92" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Search Dropdown Overlay */}
        {mobileSearchExpanded && showSearchDropdown && <div className="fixed left-0 right-0 bg-white z-[85] overflow-y-auto shadow-lg" style={{
        top: '160px',
        maxHeight: 'calc(100vh - 160px)'
      }} data-id="element-3061" data-mp="2v1lqqh1c34sd">
            <div className="p-4" data-id="element-3062" data-mp="2l35ww6ax9hf6">{getSearchContent()}</div>
          </div>}

        {/* Mobile Menu */}
        <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} data-id="element-3063" data-mp="2h4zvldsuru1n" />

        {/* Modals */}
        <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} data-id="element-3064" data-mp="fc23jk6wh5hr" />
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-3065" data-mp="adh5nrf4exwh" />
        <OrderTrackingModal isOpen={orderTrackingModalOpen} onClose={() => setOrderTrackingModalOpen(false)} data-id="element-3066" data-mp="rlrsb32sezvt" />
      </header>
    </>;
}